import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  Empty,
  Select,
  Spin,
  Tag,
} from "antd";
import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetCalendarAvailabilityQuery,
  useGetProviderConfigQuery,
  useGetVisitTypesQuery,
} from "../../redux/features/routeQuestionApi";
import {
  leadTimeBlockedDateStringsCST,
  orderedVisitTypesForProvider,
} from "../../utils/bookingFlowUtil";
import { getProviderImage } from "../../utils/providerImageUtil";

dayjs.extend(utc);
dayjs.extend(timezone);

const CATEGORY_OPTIONS = [
  { value: "newpatient", label: "New patient" },
  { value: "established", label: "Established patient" },
];

export default function AvailabilityDrawer({ formData }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(formData?.selectedState?.abbr);
  const [category, setCategory] = useState(formData?.category || "newpatient");
  const [providerId, setProviderId] = useState(
    formData?.selectedProvider?.member_id
  );
  const [visitTypeId, setVisitTypeId] = useState(
    formData?.service?.visit_type_id
  );

  const { data: configResponse, isLoading: loadingConfig } =
    useGetProviderConfigQuery();
  const config = configResponse?.data;

  const providers = useMemo(() => {
    if (!state) return [];
    return (config?.providers || []).filter((provider) =>
      (provider.supported_states || []).some(
        (abbr) => String(abbr).toUpperCase() === String(state).toUpperCase()
      )
    );
  }, [config, state]);

  const selectedProvider = useMemo(
    () =>
      providers.find(
        (provider) => String(provider.member_id) === String(providerId)
      ),
    [providers, providerId]
  );

  const {
    data: visitTypesResponse,
    isFetching: loadingVisitTypes,
    isError: visitTypesError,
  } = useGetVisitTypesQuery(
    open && providerId ? { member_id: providerId } : skipToken
  );

  const services = useMemo(() => {
    if (!selectedProvider || !visitTypesResponse?.data) return [];
    return orderedVisitTypesForProvider(
      visitTypesResponse.data,
      selectedProvider,
      category
    );
  }, [category, selectedProvider, visitTypesResponse]);

  // Availability is returned in Central Time. Using the browser's local date
  // here made the drawer and step 7 request different ranges for users outside
  // that time zone (especially around midnight).
  const availabilityRange = useMemo(() => {
    const centralToday = dayjs().tz("America/Chicago").startOf("day");
    return {
      fromDate: centralToday.format("YYYY-MM-DD"),
      toDate: centralToday.add(30, "day").format("YYYY-MM-DD"),
    };
  }, []);

  const {
    data: availabilityResponse,
    isFetching: loadingAvailability,
    isError: availabilityError,
  } = useGetCalendarAvailabilityQuery(
    open && providerId && visitTypeId
      ? {
          provider_id: providerId,
          from_date: availabilityRange.fromDate,
          to_date: availabilityRange.toDate,
          visit_type: visitTypeId,
        }
      : skipToken
  );

  const availableDates = useMemo(() => {
    // Apply the same lead-time policy step 7 enforces, otherwise this preview
    // advertises dates the scheduler will refuse to select.
    const blockedDates = leadTimeBlockedDateStringsCST();
    return (availabilityResponse?.availability?.available_slots || [])
      .map((entry) => {
        const [date, slots] = Object.entries(entry)[0] || [];
        return { date, slots: Array.isArray(slots) ? slots : [] };
      })
      .filter(
        (entry) =>
          entry.date && entry.slots.length && !blockedDates.includes(entry.date)
      );
  }, [availabilityResponse]);

  useEffect(() => {
    if (
      config &&
      state &&
      providerId &&
      !providers.some((p) => String(p.member_id) === String(providerId))
    ) {
      setProviderId(undefined);
      setVisitTypeId(undefined);
    }
  }, [config, providerId, providers, state]);

  useEffect(() => {
    if (
      visitTypeId &&
      services.length &&
      !services.some((service) => String(service.visit_type_id) === String(visitTypeId))
    ) {
      setVisitTypeId(undefined);
    }
  }, [services, visitTypeId]);

  const handleStateChange = (value) => {
    setState(value);
    setProviderId(undefined);
    setVisitTypeId(undefined);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setVisitTypeId(undefined);
  };

  const handleProviderChange = (value) => {
    setProviderId(value);
    setVisitTypeId(undefined);
  };

  return (
    <>
      <Button
        type="primary"
        size="large"
        className="availability-fab"
        icon={<CalendarOutlined />}
        onClick={() => setOpen(true)}
        aria-label="Check provider availability"
      >
        Check Availability
      </Button>

      <Drawer
        title={
          <span className="availability-drawer-title">
            <CalendarOutlined /> Provider availability
          </span>
        }
        placement="right"
        width="min(620px, 100vw)"
        open={open}
        onClose={() => setOpen(false)}
        className="availability-drawer"
        rootClassName="availability-drawer-root"
      >
        <p className="availability-intro">
          Preview providers and their available appointments for the next 30 days.
          Your current booking progress will not be changed.
        </p>

        <div className="availability-filters">
          <label>
            State
            <Select
              value={state}
              onChange={handleStateChange}
              placeholder="Select your state"
              loading={loadingConfig}
              popupClassName="availability-select-popup"
              options={(config?.states || []).map((item) => ({
                value: item.abbr,
                label: item.name,
              }))}
            />
          </label>

          <label>
            Patient type
            <Select
              value={category}
              onChange={handleCategoryChange}
              options={CATEGORY_OPTIONS}
              popupClassName="availability-select-popup"
            />
          </label>

          <label className="availability-filter-wide">
            Provider
            <Select
              value={providerId}
              onChange={handleProviderChange}
              placeholder={state ? "Select a provider" : "Select a state first"}
              disabled={!state}
              popupClassName="availability-select-popup"
              options={providers.map((provider) => ({
                value: provider.member_id,
                label: provider.display_name,
              }))}
              notFoundContent="No providers serve this state"
            />
          </label>

          <label className="availability-filter-wide">
            Service
            <Select
              value={visitTypeId}
              onChange={setVisitTypeId}
              placeholder={providerId ? "Select a service" : "Select a provider first"}
              disabled={!providerId}
              loading={loadingVisitTypes}
              popupClassName="availability-select-popup"
              options={services.map((service) => ({
                value: service.visit_type_id,
                label: service.visit_type,
              }))}
              notFoundContent={loadingVisitTypes ? <Spin size="small" /> : "No services available"}
            />
          </label>
        </div>

        {selectedProvider && (
          <div className="availability-provider-card">
            <Avatar size={48} src={getProviderImage(selectedProvider)}>
              {selectedProvider.display_name?.charAt(0)}
            </Avatar>
            <div>
              <strong>{selectedProvider.display_name}</strong>
              <div>Licensed for {state}</div>
            </div>
          </div>
        )}

        {visitTypesError && (
          <Alert type="error" showIcon message="Services could not be loaded." />
        )}

        {!visitTypeId && !visitTypesError && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a state, provider, and service to see available times."
          />
        )}

        {visitTypeId && loadingAvailability && (
          <div className="availability-loading"><Spin size="large" /></div>
        )}

        {visitTypeId && availabilityError && (
          <Alert
            type="error"
            showIcon
            message="Availability could not be loaded. Please try again."
          />
        )}

        {visitTypeId && !loadingAvailability && !availabilityError && (
          <>
            <div className="availability-results-heading">
              <h3>Available appointments</h3>
              <Tag color="orange">Central Time (CT)</Tag>
            </div>
            {!availableDates.length ? (
              <Empty description="No appointments are available in the next 30 days." />
            ) : (
              <div className="availability-date-list">
                {availableDates.map(({ date, slots }) => (
                  <section className="availability-date-card" key={date}>
                    <div className="availability-date-heading">
                      <CalendarOutlined />
                      <span>{dayjs(date).format("dddd, MMMM D, YYYY")}</span>
                      <Tag>{slots.length} {slots.length === 1 ? "slot" : "slots"}</Tag>
                    </div>
                    <div className="availability-slot-list">
                      {slots.map((slot, index) => (
                        <span
                          className="availability-slot"
                          key={`${date}-${slot.start_time}-${slot.end_time}-${index}`}
                        >
                          <ClockCircleOutlined /> {slot.start_time} – {slot.end_time}
                        </span>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
